import {createFileRoute, notFound} from '@tanstack/react-router'
import Base64 from 'crypto-js/enc-base64'
import Utf8 from 'crypto-js/enc-utf8'

import {localeAtom} from '@/lib/locale/locale'
import {getProductDetailsQueryOptions} from '@/lib/product/hooks/useProductDetails'
import preloadProduct from '@/lib/product/utils/preloadProduct'

const ProductUI = lazy(async () => import('@/views/Product/ProductUI'))

export interface ProductId {
  retailer: string
  id: string
}

export const Route = createFileRoute('/_shop/product/$productId/')({
  parseParams: rawParams => {
    let productId = rawParams.productId

    // Try to parse the productId as it was in base64, if failed, fallback to parse as string
    try {
      productId = Base64.parse(productId).toString(Utf8)
    } catch {
      // do nothing
    }

    const [retailer, ...ids] = productId.split('-')
    const id = ids.join('')

    // eslint-disable-next-line @typescript-eslint/only-throw-error -- this is desired behavior
    if (!retailer) throw notFound()

    return {
      productId: {
        retailer,
        id,
      },
    }
  },
  stringifyParams: params => {
    return {
      productId: Base64.stringify(
        Utf8.parse(`${params.productId.retailer}-${params.productId.id}`),
      ),
    }
  },
  loader: async ({context: {queryClient, store}, params: {productId}, preload}) => {
    const locale = store.get(localeAtom)
    if (preload) return preloadProduct(productId, locale, queryClient)
    return queryClient.ensureQueryData(
      getProductDetailsQueryOptions({retailer: productId.retailer, id: productId.id, locale}),
    )
  },
  pendingComponent: () => (
    <Suspense fallback={null}>
      <ProductUI isLoading />
    </Suspense>
  ),
})
